import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { pmGet, pmPost } from '../api';
import './PortfolioExceptions.css';

/**
 * Portfolio Exceptions — the one screen that looks across every programme at once.
 *
 * Cloned from `ai_readiness_template/views/roadmap-execution/exceptions.ejs`. The markup,
 * class names, column widths, copy, bucket thresholds and colours are the source's; the
 * stylesheet carries its values verbatim. The two places this had to differ are both recorded
 * where they occur: the header lock is measured rather than typed (the shell has no fixed
 * chrome height), and the charts are CSS rather than Chart.js (no charting dependency, and
 * the CSP blocks the CDN the source loads it from).
 *
 * ── WHAT THIS SCREEN IS FOR ────────────────────────────────────────────────────────────
 *
 * A client reviewing ~90 initiatives a week reported that most of the review time went on
 * FINDING problems rather than fixing them. Every other screen in this module starts with a
 * project picker; this one does not, and that is its whole point — it reads across everything
 * you may see and tells you where to go.
 *
 * ── WHY THE DRAWER, AND NOT A LINK ─────────────────────────────────────────────────────
 *
 * The first version of this screen made "Open" navigate to the Execution Control Center. That
 * was wrong on its own premise: you built this to stop opening ninety initiatives, so sending
 * the user into a project-scoped screen to change one date puts the navigation straight back.
 * Following up with ten people on a Monday would be ten round trips, each reloading a whole
 * programme. Editing happens in a drawer on THIS screen; the Control Center link stays,
 * inside the drawer, for when the full programme context is genuinely wanted.
 *
 * ── THE COUNTS ARE PER-VIEWER ──────────────────────────────────────────────────────────
 *
 * The server filters to the delivery projects the viewer may see. Two people comparing this
 * screen will legitimately see different totals — which is why the help panel and the footnote
 * both say so. Without being told, they will conclude one of the two screens is broken.
 */

type Dict = Record<string, unknown>;

/**
 * A multi-select filter: a button that reports what is selected, and a panel of rows.
 *
 * NOT `<select multiple>` — it is not a pull-down, it behaves differently on every
 * platform, and it matches nothing else in this app. The established control here is a
 * button + panel, and its two-mode click is the part that makes it usable:
 *
 *   · plain click  — select just that one, apply immediately, close;
 *   · Ctrl/Cmd-click — toggle it, keep the panel open to carry on picking.
 *
 * The button keeps saying what it is doing when collapsed ("2 of 13 selected"), and Clear
 * only appears while something is set, so an unset filter has no affordance to undo.
 */
function PxFilter({
  label,
  options,
  selected,
  onChange,
  open,
  onOpen,
}: {
  label: string;
  options: string[];
  selected: Record<string, true>;
  onChange: (next: Record<string, true>) => void;
  open: boolean;
  onOpen: (open: boolean) => void;
}) {
  const n = Object.keys(selected).length;
  const text = n === 0 ? `All ${label.toLowerCase()}` : `${n} of ${options.length} selected`;

  const click = (id: string, ev: React.MouseEvent) => {
    if (ev.ctrlKey || ev.metaKey) {
      const next = { ...selected };
      if (next[id]) delete next[id];
      else next[id] = true;
      onChange(next);           // stay open — the user is still choosing
    } else {
      onChange({ [id]: true }); // just this one, applied now
      onOpen(false);
    }
  };

  return (
    <span className="px-filter">
      <button type="button" className="px-filter-btn" onClick={() => onOpen(!open)}>
        {label}: <b>{text}</b> <span className="px-caret">▾</span>
      </button>
      {n ? (
        <button type="button" className="px-lnk" onClick={() => onChange({})}>
          Clear
        </button>
      ) : null}

      {open ? (
        <>
          {/* Click-away closes and therefore applies — the source's behaviour. */}
          <div className="px-filter-scrim" onClick={() => onOpen(false)} />
          <div className="px-filter-panel" role="listbox" aria-label={label}>
            <div className="px-filter-head">
              <button type="button" onClick={() => onChange(Object.fromEntries(options.map((o) => [o, true])) as Record<string, true>)}>
                All {label.toLowerCase()}
              </button>
              <button type="button" onClick={() => onChange({})}>
                Clear
              </button>
              <span className="px-filter-hint">Ctrl-click to pick several</span>
            </div>
            <div className="px-filter-list">
              {options.length === 0 ? (
                <div className="px-filter-empty">Nothing to filter yet.</div>
              ) : (
                options.map((o) => (
                  <div
                    key={o}
                    role="option"
                    aria-selected={Boolean(selected[o])}
                    className={`px-filter-row${selected[o] ? ' on' : ''}`}
                    onClick={(ev) => click(o, ev)}
                  >
                    <span className="px-filter-tick">{selected[o] ? '✓' : ''}</span>
                    {o}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      ) : null}
    </span>
  );
}

/** The route's short aliases, kept as-is — the payload carries hundreds of these. */
interface ExcRow {
  i: number;
  pj: string;
  op: string | null;
  on: string;
  n: string;
  ph: string;
  t: string;
  ro: string | null;
  pe: string;
  em: string;
  pg: number;
  sd: string | null;
  no: string | null;
  du: string | null;
  dy: number | null;
  st: number | null;
  b: string;
}

type Lens = 'person' | 'initiative' | 'flat' | 'dash';

/*
 * The five buckets. A TIME RAMP, NOT A RAINBOW — red past, amber imminent, blue approaching,
 * green comfortable, slate for absence of information. Two earlier attempts are worth not
 * repeating: two identical ambers for Urgent and Due soon (indistinguishable), and an olive
 * halfway shade between amber and green (red → amber → olive → green is not a scale anyone
 * reads). Slate for "Not updated" because it is not a deadline state at all.
 */
const PX_B = [
  { k: 'overdue', lbl: 'Overdue', c: '#b3261e', cap: 'due date passed' },
  { k: 'urgent', lbl: 'Urgent', c: '#b26a00', cap: 'due within 7 days' },
  { k: 'soon', lbl: 'Due soon', c: '#1565c0', cap: 'due in 8-14 days' },
  { k: 'upcoming', lbl: 'Upcoming', c: '#2e6b3e', cap: 'due in 15-30 days' },
  { k: 'stale', lbl: 'Not updated', c: '#5c6b7a', cap: 'no change for 14 days' },
] as const;

const PX_C: Record<string, string> = {
  overdue: '#b3261e',
  urgent: '#b26a00',
  soon: '#1565c0',
  upcoming: '#2e6b3e',
  later: '#93a3b1',
  nodate: '#93a3b1',
};

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));

const pxIni = (n: string) =>
  n
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const pxDue = (d: number | null) =>
  d == null ? 'no date' : d < 0 ? `${Math.abs(d)} days overdue` : d === 0 ? 'due today' : `in ${d} days`;

/**
 * The JS half of `projectOrderBy` — numeric ids numerically and ascending, ahead of coded
 * ones; coded ones alphabetically; blanks last. Applied part by part to a composite
 * "<project> · <initiative>" key so equal groups never come out in an arbitrary order.
 */
function pxCmpOne(a: unknown, b: unknown): number {
  const A = s(a).trim();
  const B = s(b).trim();
  if (!A && !B) return 0;
  if (!A) return 1;
  if (!B) return -1;
  const nA = /^[0-9]+$/.test(A);
  const nB = /^[0-9]+$/.test(B);
  if (nA && nB) return Number(A) - Number(B);
  if (nA !== nB) return nA ? -1 : 1;
  return A.localeCompare(B, undefined, { numeric: true, sensitivity: 'base' });
}
function pxCmpKey(a: string, b: string): number {
  const A = a.split(' · ');
  const B = b.split(' · ');
  for (let i = 0; i < Math.max(A.length, B.length); i++) {
    const r = pxCmpOne(A[i], B[i]);
    if (r) return r;
  }
  return 0;
}

/**
 * Follow up — a reminder to the person who owns the item.
 *
 * The platform has no mailer and no scheduler, so rather than a dead button this opens the
 * user's own mail client with the message already written. That is most of the value of
 * "reminder automation" at none of its cost; full automation still needs a mailer, an outbox
 * drain and an external scheduler.
 *
 * It is called **Follow up**, not "Chase", because the action register already speaks this
 * language: *Follow-up* is one of its ten action types, `followup_date` is the field that
 * drives chasing, and the subject line below reads `Follow-up:`. One word across all four is
 * worth more than a punchier label on one of them.
 *
 * Where there is no address the mail still opens with the recipient blank rather than failing.
 */
function pxFollowUp(r: ExcRow): string {
  const where = r.pj + (r.op ? ` / ${r.op}` : ' (programme-level)');
  const state =
    r.dy == null
      ? 'has no due date'
      : r.dy < 0
        ? `was due ${r.du} — ${Math.abs(r.dy)} days ago`
        : `is due ${r.du} (${pxDue(r.dy)})`;
  const body =
    `Hi ${s(r.pe).split(' ')[0]},\n\n` +
    `Checking in on "${r.n}" (${where}).\n` +
    `It ${state} and is recorded at ${r.pg}% complete.\n\n` +
    `Could you let me know where it stands, and whether the date still holds?\n\nThanks`;
  return (
    `mailto:${encodeURIComponent(r.em || '')}` +
    `?subject=${encodeURIComponent(`Follow-up: ${r.n} — ${where}`)}` +
    `&body=${encodeURIComponent(body)}`
  );
}

/** The source's bar chart, as CSS — same measures, groupings, orderings and colours. */
function BarChart({ data, color }: { data: Record<string, number>; color: string }) {
  const labels = Object.keys(data);
  if (!labels.length) return <div className="cempty">—</div>;
  const max = Math.max(1, ...labels.map((l) => data[l] ?? 0));
  return (
    <div>
      {labels.map((l) => (
        <div className="cbar-row" key={l}>
          <div className="cbar-lbl" title={l}>
            {l}
          </div>
          <div className="cbar-track">
            <div
              className="cbar-fill"
              style={{ width: `${((data[l] ?? 0) / max) * 100}%`, background: color }}
            />
          </div>
          <div className="cbar-val">{data[l]}</div>
        </div>
      ))}
    </div>
  );
}

/** The source's doughnut, as a proportional strip plus its legend. Same slices, same counts. */
function Doughnut({ slices }: { slices: { k: string; v: number; c: string }[] }) {
  const total = slices.reduce((a, x) => a + x.v, 0);
  if (!total) return <div className="cempty">—</div>;
  return (
    <div>
      <div className="cdough-strip">
        {slices.map((x) => (
          <div
            className="cdough-seg"
            key={x.k}
            style={{ width: `${(x.v / total) * 100}%`, background: x.c }}
            title={`${x.k}: ${x.v}`}
          />
        ))}
      </div>
      <div className="cdough-legend">
        {slices.map((x) => (
          <span className="cdough-key" key={x.k}>
            <span className="cdough-box" style={{ background: x.c }} />
            {x.k} <span className="cdough-n">{x.v}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Biggest first, and never more than 10 bars — a chart with 40 labels is unreadable. */
function countBy(rows: ExcRow[], fn: (r: ExcRow) => string): Record<string, number> {
  const m: Record<string, number> = {};
  rows.forEach((r) => {
    const k = fn(r) || '—';
    m[k] = (m[k] ?? 0) + 1;
  });
  return Object.keys(m)
    .sort((a, b) => (m[b] ?? 0) - (m[a] ?? 0))
    .slice(0, 10)
    .reduce<Record<string, number>>((o, k) => {
      o[k] = m[k] ?? 0;
      return o;
    }, {});
}

const EXCEPTIONS_HELP: HelpContent = {
  title: 'Portfolio Exceptions',
  icon: '🎯',
  purpose:
    'The one screen that looks <strong>across every programme at once</strong> and shows only what needs attention. Instead of opening each initiative to hunt for problems, you start here: what is <strong>overdue</strong>, what is <strong>due next</strong>, and <strong>who</strong> you need to follow up with. Every other execution screen is scoped to one programme &mdash; this one is not.',
  doHere: [
    'Read the five <strong>statistical cards</strong> across the top &mdash; a live count of open work items in each exception bucket, across every programme you have access to.',
    '<strong>Click a card to filter</strong> the list to that bucket; click it again to clear.',
    'Switch how the list is grouped: <strong>By owner</strong> (who to follow up with), <strong>By initiative</strong> (where the trouble is), or <strong>Flat list</strong> (everything in date order).',
    'Open the <strong>Dashboard</strong> tab for the same picture drawn rather than listed &mdash; counts by bucket, owner, initiative, phase, how overdue things are, and the deliverable/activity split. It obeys the card filter, so the charts always show the same population as the list.',
    'Expand a group to see the individual work items &mdash; each with its due date, how far past or short of it you are, and progress.',
    '<strong>Open</strong> opens the work item in a drawer on this screen, so you can update progress, dates and notes without leaving the list.',
  ],
  fits: 'Every execution screen &rarr; <strong>Portfolio Exceptions</strong> &rarr; Execution Control Center / Action Register',
  firstTime:
    'Leave the filters alone and read the <strong>By owner</strong> list top to bottom &mdash; it is already sorted worst-first. The <strong>Dashboard</strong> tab shows the same data as charts.',
  sections: [
    {
      label: 'The five buckets',
      type: 'defs',
      items: [
        {
          term: 'Overdue',
          color: '#b3261e',
          def: 'The due date has passed and the work item is not complete. Sorted first everywhere on this screen.',
        },
        { term: 'Urgent', color: '#b26a00', def: 'Due within the next <strong>7 days</strong> and not complete.' },
        { term: 'Due soon', color: '#1565c0', def: 'Due in <strong>8&ndash;14 days</strong> and not complete.' },
        {
          term: 'Upcoming',
          color: '#2e6b3e',
          def: 'Due in <strong>15&ndash;30 days</strong>. Shown in green because it is visible rather than a problem &mdash; it is the work to plan, not to follow up on yet.',
        },
        {
          term: 'Not updated',
          color: '#5c6b7a',
          def: 'Nothing has been written to the work item for <strong>14 days</strong>. See below for what this can and cannot tell you.',
        },
      ],
      note: 'A work item counts as <strong>open</strong> while its progress is below 100%. Anything due beyond 30 days is deliberately not shown: this screen is about the next month, not the whole plan.',
    },
    {
      label: 'What “Not updated” can and cannot tell you',
      type: 'html',
      html: 'Work items now record <strong>when they were last changed</strong>, maintained automatically on every write. So this card answers &ldquo;nothing has happened here for two weeks&rdquo; &mdash; but only from the day that tracking was switched on (12 August 2026).<br><br>A work item that has not been touched <em>since then</em> carries no last-changed date at all, and is <strong>not</strong> counted: no signal is not the same as a stale signal, and counting it would report the whole portfolio as abandoned on day one. Those items join the count the first time anyone edits them. Staleness from before that date is not reconstructable.',
    },
    {
      label: 'Owner means the person, not the role',
      type: 'html',
      html: "<strong>By owner</strong> groups by the <strong>person assigned</strong> to the work item, with their roles shown underneath. So one row is one conversation &mdash; everything you need to raise with that person, across every programme and initiative they touch, in one place.<br><br>Where nobody is assigned, the work item's <em>typical owner</em> role is used instead, and it appears under <strong>Unassigned</strong> if there is neither.",
    },
    {
      label: 'What you see is what you may see',
      type: 'html',
      html: 'The counts and the list are filtered to the <strong>delivery projects you have access to</strong>. Two people opening this screen can legitimately see different totals &mdash; the numbers are per-viewer, not global.',
    },
  ],
  links: [
    { label: 'Execution Control Center →', href: '/project_management/program-management/delivery-execution/execution-control-center' },
    { label: 'Action Register →', href: '/project_management/program-management/delivery-execution/action-register' },
  ],
};

export function PortfolioExceptions() {
  const [rows, setRows] = useState<ExcRow[]>([]);
  const [asAt, setAsAt] = useState('');
  const [todayIso, setTodayIso] = useState('');
  const [staleDays, setStaleDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [lens, setLens] = useState<Lens>('person');
  const [bucket, setBucket] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, 1>>({});
  const [allOpen, setAllOpen] = useState(false);

  /* Drawer */
  const [cur, setCur] = useState<ExcRow | null>(null);
  /*
   * §13.3 — THE PERCENTAGE FIELD, and the most damaging bug in this whole build.
   *
   * In the source, removing the slider took with it the line that wrote the value back into
   * the number box. The drawer then opened with the box EMPTY, and saving sent 0 — zeroing
   * progress on live records (83% → 0, 71% → 0).
   *
   * That exact failure cannot happen here: the input is bound to state and `openItem` seeds it
   * from the row. But the naive React form of the same hazard can. `parseInt('') || 0` turns a
   * momentarily-empty field into 0, so selecting the contents to retype a number would snap the
   * field to 0 — and a Save at that instant writes it.
   *
   * So the field holds RAW TEXT and is parsed only where a number is actually needed. An empty
   * or unparseable field is not 0; it is "no value yet", Save is refused, and the reason is on
   * screen. A control that cannot be cleared to retype is hostile; one that silently reads
   * cleared as zero is dangerous.
   */
  const [dwPct, setDwPct] = useState('0');
  const [dwDue, setDwDue] = useState('');
  const [dwNotes, setDwNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  /*
   * THE HEIGHT IS MEASURED, NOT TYPED. `height: 100%` does not work in this shell — `<main>`
   * carries `min-height: 100vh` and grows with its content, so a percentage resolves against
   * nothing and the window scrolls. A callback ref rather than `useRef` because the page
   * mounts after a loading gate, and an effect keyed on a `useRef` would run once against
   * null and silently never apply a height.
   */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  useEffect(() => {
    pmGet<{
      success: boolean;
      rows: ExcRow[];
      asAt: string;
      todayIso: string;
      staleDays: number;
      error?: string;
    }>('/exceptions')
      .then((d) => {
        if (!d.success) throw new Error(d.error ?? 'failed');
        setRows(d.rows ?? []);
        setAsAt(d.asAt);
        setTodayIso(d.todayIso);
        setStaleDays(d.staleDays ?? 14);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  /* Multi-select filters. Empty object = no constraint. */
  const [projSel, setProjSel] = useState<Record<string, true>>({});
  const [oppSel, setOppSel] = useState<Record<string, true>>({});
  const [openPanel, setOpenPanel] = useState<'proj' | 'opp' | null>(null);

  /** Stale is a SEPARATE axis from the due-date buckets — an item can be both. */
  const isStale = useCallback((r: ExcRow) => r.st != null && r.st >= staleDays, [staleDays]);

  /*
   * ── SCOPE: THE FILTER IS APPLIED ONCE, AT THE SOURCE ───────────────────────────────
   *
   * Every consumer — the KPI cards, all three lists and the dashboard — reads through
   * `scoped`, never through `rows`. Filtering inside each view is how a card ends up
   * disagreeing with the list beneath it, and on a screen whose whole purpose is "these
   * numbers need attention", two different numbers is worse than none.
   *
   * Empty selection means no constraint, so the screen behaves exactly as before until a
   * filter is set — nothing here gates on data existing.
   */
  const scoped = useMemo(() => {
    const np = Object.keys(projSel).length;
    const no = Object.keys(oppSel).length;
    if (!np && !no) return rows;
    return rows.filter((x) => {
      if (np && !projSel[x.pj]) return false;
      if (no && !oppSel[String(x.op ?? '')]) return false;
      return true;
    });
  }, [rows, projSel, oppSel]);

  /** Beyond 30 days is not an exception — it never appears on this screen. */
  const visible = useMemo(() => {
    const base = scoped.filter((x) => x.b !== 'later');
    if (!bucket) return base;
    if (bucket === 'stale') return base.filter(isStale);
    return base.filter((x) => x.b === bucket);
  }, [scoped, bucket, isStale]);

  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    PX_B.forEach((b) => {
      m[b.k] =
        b.k === 'stale' ? scoped.filter(isStale).length : scoped.filter((x) => x.b === b.k).length;
    });
    return m;
  }, [scoped, isStale]);

  /* The project list is every project present in the payload. */
  const projOptions = useMemo(
    () => [...new Set(rows.map((r) => r.pj).filter(Boolean))].sort(),
    [rows],
  );

  /*
   * THE OPPORTUNITY LIST IS REBUILT FROM THE SELECTED PROJECTS, NOT HIDDEN.
   *
   * `option[hidden]` is not enough — a hidden option is still keyboard-selectable and
   * still reported by `.value`. Rebuilding means an opportunity outside the current
   * project selection cannot be chosen, and the effect below DROPS any that is already
   * selected when the project selection changes.
   */
  const oppOptions = useMemo(() => {
    const np = Object.keys(projSel).length;
    const src = np ? rows.filter((r) => projSel[r.pj]) : rows;
    return [...new Set(src.map((r) => String(r.op ?? '')).filter(Boolean))].sort();
  }, [rows, projSel]);

  useEffect(() => {
    const allowed = new Set(oppOptions);
    setOppSel((cur) => {
      const next: Record<string, true> = {};
      let dropped = false;
      Object.keys(cur).forEach((k) => {
        if (allowed.has(k)) next[k] = true;
        else dropped = true;
      });
      return dropped ? next : cur;
    });
  }, [oppOptions]);

  const groups = useMemo(() => {
    const m: Record<string, ExcRow[]> = {};
    visible.forEach((r) => {
      // By initiative reads "<project id> · <initiative id>" — the two identifiers, in that order.
      const k = lens === 'person' ? r.pe : `${r.pj} · ${r.op || 'programme-level'}`;
      (m[k] = m[k] ?? []).push(r);
    });
    return (
      Object.keys(m)
        .map((k) => {
          const v = m[k]!;
          const roles: string[] = [];
          v.forEach((x) => {
            if (x.ro && !roles.includes(x.ro)) roles.push(x.ro);
          });
          const n = (b: string) => v.filter((x) => x.b === b).length;
          return {
            key: k,
            rows: v,
            overdue: n('overdue'),
            urgent: n('urgent'),
            soon: n('soon'),
            upcoming: n('upcoming'),
            sub: lens === 'person' ? roles.slice(0, 3).join(' · ') : (v[0]!.on || ''),
          };
        })
        /* Worst first — the whole point of the screen is that you read from the top. Ties break
           on the id in the app's normal ascending order. */
        .sort((a, b) => b.overdue - a.overdue || b.rows.length - a.rows.length || pxCmpKey(a.key, b.key))
    );
  }, [visible, lens]);

  const resetView = () => {
    setOpen({});
    setAllOpen(false);
    const b = document.querySelector('.px-body');
    if (b) b.scrollTop = 0;
  };
  const chooseLens = (l: Lens) => {
    setLens(l);
    resetView();
  };
  const chooseBucket = (b: string) => {
    setBucket((prev) => (prev === b ? null : b));
    resetView();
  };
  const toggleAll = () => {
    setAllOpen((p) => !p);
    setOpen({});
    const b = document.querySelector('.px-body');
    if (b) b.scrollTop = 0;
  };
  const toggleGroup = (key: string) => {
    setAllOpen(false);
    setOpen((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = 1;
      return next;
    });
  };

  /* ---- drawer -------------------------------------------------------------------------- */

  const openItem = (r: ExcRow) => {
    setCur(r);
    setDwPct(String(r.pg ?? 0));
    setDwDue(r.du ?? '');
    setDwNotes(r.no ?? '');
    setDirty(false);
  };

  const closeDrawer = useCallback(() => {
    if (dirty && !window.confirm('Discard your changes to this work item?')) return;
    setCur(null);
    setDirty(false);
  }, [dirty]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && cur) closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [cur, closeDrawer]);

  /** Status is DERIVED from the percentage — one field, no vocabulary. */
  const derived = (pct: number) => (pct <= 0 ? 'Not Started' : pct >= 100 ? 'Done' : 'In Progress');
  const derivedColour = (pct: number) => (pct <= 0 ? '#93a3b1' : pct >= 100 ? '#2e6b3e' : '#b26a00');

  /** The field's numeric value, or null when it is empty or unparseable. Never silently 0. */
  const pctNum = (() => {
    const t = dwPct.trim();
    if (!t || !/^\d{1,3}$/.test(t)) return null;
    const n = parseInt(t, 10);
    return n >= 0 && n <= 100 ? n : null;
  })();

  const setPct = (v: string) => {
    // Digits only, and never longer than 3 — so 100 is reachable and 1000 is not.
    setDwPct(v.replace(/[^\d]/g, '').slice(0, 3));
    setDirty(true);
  };

  const eccHref = (r: ExcRow) =>
    `/project_management/program-management/delivery-execution/execution-control-center?project=${encodeURIComponent(r.pj)}` +
    (r.op ? `&opportunity=${encodeURIComponent(r.op)}` : '') +
    `&item=${encodeURIComponent(r.i)}`;

  const save = async () => {
    if (!cur) return;
    /* Refuse rather than default. Writing 0 for "the user has not finished typing" is the
       exact failure §13.3 records, and a guard is cheaper than the incident. */
    if (pctNum === null) return;
    setSaving(true);
    const pct = pctNum;
    const due = dwDue || null;
    const notes = dwNotes || null;
    try {
      /*
       * THE EXISTING ENDPOINT, deliberately. It already handles the Agile branch
       * (`agile_deliverables` vs `project_deliverables`); a second update path here would
       * drift from it the first time either changed.
       */
      const d = await pmPost<{ success: boolean; error?: string }>('/tracker/deliverable/update', {
        id: cur.i,
        delivery_project_id: cur.pj,
        progress: pct,
        status: derived(pct),
        notes,
        delivered_by_date: due,
        /*
         * §12.12 — NAME THE SCREEN. This endpoint is shared with the Execution Control Center,
         * and the audit entry it writes is only as good as the origin it records: without this
         * every Monday-review edit made here would be filed against the Control Center, and the
         * trail would say the work was done somewhere nobody was.
         */
        screen: 'exceptions',
      } as Dict);
      if (!d.success) throw new Error(d.error ?? 'Save failed');
    } catch (e) {
      setSaving(false);
      window.alert(`Could not save: ${(e as Error).message}`);
      return;
    }

    /*
     * Re-bucket IN PLACE. A 100% item leaves the screen entirely — it is no longer open.
     * The thresholds below are duplicated from the route on purpose (the source does the
     * same); change one and change the other. `todayIso` came from the server so this uses
     * the same "today" the SQL did.
     */
    setRows((prev) => {
      if (pct >= 100) return prev.filter((x) => x.i !== cur.i);
      return prev.map((x) => {
        if (x.i !== cur.i) return x;
        const next: ExcRow = { ...x, pg: pct, du: due, no: notes };
        if (due) {
          const days = Math.round(
            (new Date(`${due}T00:00:00`).getTime() - new Date(`${todayIso}T00:00:00`).getTime()) /
              86400000,
          );
          next.dy = days;
          next.b =
            days < 0 ? 'overdue' : days <= 7 ? 'urgent' : days <= 14 ? 'soon' : days <= 30 ? 'upcoming' : 'later';
        }
        // The write we just made is what `updated_at` records, so it is no longer stale.
        next.st = 0;
        return next;
      });
    });

    setSaving(false);
    setDirty(false);
    setCur(null);
    setToast('Work item updated');
    setTimeout(() => setToast(null), 2200);
  };

  /* ---- rendering ----------------------------------------------------------------------- */

  /* name + owner always total 600px, so Due / Progress / actions sit at the same x in every
     lens. When the owner column is dropped its width goes to the name, so nothing else moves.
     The trailing bare <col> is a spacer soaking up leftover width, which keeps the real
     columns packed left instead of the progress bar stretching and stranding the buttons. */
  const cols = (
    <colgroup>
      <col style={{ width: lens === 'person' ? 600 : 430 }} />
      {lens === 'person' ? null : <col style={{ width: 170 }} />}
      <col style={{ width: 125 }} />
      <col style={{ width: 165 }} />
      <col style={{ width: 140 }} />
      <col />
    </colgroup>
  );

  const table = (rs: ExcRow[]) => (
    <div className="px-tw">
      <table className="px-tbl">
        {cols}
        <thead>
          <tr>
            <th>Work item</th>
            {lens === 'person' ? null : <th>Owner</th>}
            <th className="w1">Due</th>
            <th className="w1">Progress</th>
            <th className="w1" />
            <th className="px-pad" />
          </tr>
        </thead>
        <tbody>
          {rs.map((r) => {
            const c = PX_C[r.b] ?? '#93a3b1';
            const activity = r.t === 'Activity';
            return (
              <tr key={r.i}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`px-tg ${activity ? 'A' : 'D'}`}>{activity ? 'A' : 'D'}</span>
                    <span className="px-wi">{r.n}</span>
                  </div>
                  <div className="px-mt">
                    {r.pj}
                    {r.op ? ` · ${r.op}` : ' · programme-level'}
                    {` · ${s(r.ph).replace(/_/g, ' ')}`}
                    {isStale(r) ? ` · not updated for ${r.st} days` : ''}
                  </div>
                </td>
                {lens === 'person' ? null : (
                  <td>
                    <div className="px-wi">{r.pe}</div>
                    <div className="px-mt">{r.ro ?? ''}</div>
                  </td>
                )}
                <td className="px-dd w1" style={{ '--c': c } as React.CSSProperties}>
                  {r.du ?? '—'}
                  <small>{pxDue(r.dy)}</small>
                </td>
                <td className="w1">
                  <div className="px-pw">
                    <span className="px-pb" style={{ '--c': c } as React.CSSProperties}>
                      <i style={{ width: `${r.pg}%` }} />
                    </span>
                    <span className="px-pp">{r.pg}%</span>
                  </div>
                </td>
                <td className="w1">
                  <div className="px-acts">
                    <button type="button" className="px-mn" onClick={() => openItem(r)}>
                      Open
                    </button>
                    {/* Follow up sits ON THE ROW as well as in the drawer, so you can mail
                        someone without opening the work item. */}
                    <a className="px-mn" href={pxFollowUp(r)}>
                      Follow up
                    </a>
                  </div>
                </td>
                <td className="px-pad" />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const bucketLabel = PX_B.find((x) => x.k === bucket)?.lbl ?? '';

  const dash = () => {
    const rs = visible;
    const n = (b: string) => rs.filter((x) => x.b === b).length;
    const overdue = rs.filter((x) => x.b === 'overdue');
    const avgPct = rs.length ? Math.round(rs.reduce((a, x) => a + (x.pg || 0), 0) / rs.length) : 0;
    const people = new Set(rs.map((x) => x.pe)).size;

    /* Aging buckets keep their DECLARED order — building them as a literal rather than
       sorting by size is what stops "90+ days" landing in the middle. */
    const age: Record<string, number> = { '1-7 days': 0, '8-30 days': 0, '31-90 days': 0, '90+ days': 0 };
    overdue.forEach((x) => {
      const d = Math.abs(x.dy ?? 0);
      if (d <= 7) age['1-7 days']!++;
      else if (d <= 30) age['8-30 days']!++;
      else if (d <= 90) age['31-90 days']!++;
      else age['90+ days']!++;
    });

    const del = rs.filter((x) => x.t !== 'Activity').length;
    const kpi = (c: string, v: string | number, l: string, sub: string) => (
      <div className="px-kpi" style={{ '--c': c } as React.CSSProperties} key={l}>
        <div className="lbl">{l}</div>
        <div className="val">{v}</div>
        <div className="sub">{sub}</div>
      </div>
    );

    return (
      <>
        <div className="px-kpis">
          {kpi('#b3261e', n('overdue'), 'Overdue', 'due date passed')}
          {kpi('#b26a00', n('urgent'), 'Urgent', 'within 7 days')}
          {kpi('#1565c0', n('soon'), 'Due soon', '8-14 days')}
          {kpi('#2e6b3e', n('upcoming'), 'Upcoming', '15-30 days')}
          {kpi('#1c2733', people, 'Owners', 'people to follow up with')}
          {kpi('#1c2733', `${avgPct}%`, 'Avg progress', 'across these items')}
        </div>
        <div className="px-charts">
          <div className="px-chart">
            <h4>By exception bucket</h4>
            <div className="box">
              <Doughnut
                slices={[
                  { k: 'Overdue', v: n('overdue'), c: '#b3261e' },
                  { k: 'Urgent', v: n('urgent'), c: '#b26a00' },
                  { k: 'Due soon', v: n('soon'), c: '#1565c0' },
                  { k: 'Upcoming', v: n('upcoming'), c: '#2e6b3e' },
                ]}
              />
            </div>
          </div>
          <div className="px-chart">
            <h4>Open items by owner</h4>
            <div className="box">
              <BarChart data={countBy(rs, (r) => r.pe)} color="#1f3864" />
            </div>
          </div>
          <div className="px-chart">
            <h4>By initiative</h4>
            <div className="box">
              <BarChart
                data={countBy(rs, (r) => r.pj + (r.op ? ` · ${r.op}` : ' · programme'))}
                color="#1565c0"
              />
            </div>
          </div>
          <div className="px-chart">
            <h4>By phase</h4>
            <div className="box">
              <BarChart data={countBy(rs, (r) => s(r.ph).replace(/_/g, ' '))} color="#b26a00" />
            </div>
          </div>
          <div className="px-chart">
            <h4>How overdue</h4>
            <div className="box">
              <BarChart data={age} color="#b3261e" />
            </div>
          </div>
          <div className="px-chart">
            <h4>Deliverables vs activities</h4>
            <div className="box">
              <Doughnut
                slices={[
                  { k: 'Deliverable', v: del, c: '#37507a' },
                  { k: 'Activity', v: rs.length - del, c: '#8a4b45' },
                ]}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  if (loading) return <div style={{ padding: 24, color: '#93a3b1' }}>Loading…</div>;

  return (
    <div className="px-page" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      {/* ANCHORED: title, statistical cards and the grouping tabs. Nothing here scrolls. */}
      <div className="px-head">
        <h1 className="page-header__title" style={{ display: 'inline-block', marginRight: 12 }}>
          Portfolio Exceptions
        </h1>
        <span className="px-sub">All projects · as at {asAt}</span>
        <HelpPanel help={EXCEPTIONS_HELP} />

        {error ? (
          <div style={{ color: '#c62828', fontSize: 12.5, marginTop: 8 }}>{error}</div>
        ) : null}

        <div className="px-stats">
          {PX_B.map((b) => (
            <button
              type="button"
              className="px-stat"
              key={b.k}
              style={{ '--c': b.c } as React.CSSProperties}
              aria-pressed={bucket === b.k}
              onClick={() => chooseBucket(b.k)}
            >
              <span className="lb">{b.lbl}</span>
              <span className="vl">{counts[b.k]}</span>
              <span className="cp">{b.cap}</span>
            </button>
          ))}
        </div>

        <div className="px-tabs" role="tablist" aria-label="Group by">
          {(
            [
              ['person', 'By owner'],
              ['initiative', 'By initiative'],
              ['flat', 'Flat list'],
              ['dash', 'Dashboard'],
            ] as [Lens, string][]
          ).map(([k, label]) => (
            <button
              type="button"
              className={`px-tab${lens === k ? ' on' : ''}`}
              data-label={label}
              key={k}
              onClick={() => chooseLens(k)}
            >
              <span>{label}</span>
            </button>
          ))}
          <span className="px-tools">
            {/* Filters live in the tab row so they read as scoping the whole screen —
                cards, lists and dashboard alike — rather than just the list below. */}
            <PxFilter
              label="Project"
              options={projOptions}
              selected={projSel}
              onChange={setProjSel}
              open={openPanel === 'proj'}
              onOpen={(o) => setOpenPanel(o ? 'proj' : null)}
            />
            <PxFilter
              label="Opportunity"
              options={oppOptions}
              selected={oppSel}
              onChange={setOppSel}
              open={openPanel === 'opp'}
              onOpen={(o) => setOpenPanel(o ? 'opp' : null)}
            />
            <span className="px-cnt">
              {lens === 'dash' ? (
                <>
                  <b>{visible.length}</b> items charted
                </>
              ) : bucket ? (
                <>
                  <b>{visible.length}</b> {bucketLabel.toLowerCase()}
                </>
              ) : (
                <>
                  <b>{visible.length}</b> items need attention within 30 days
                </>
              )}
            </span>
            {bucket ? (
              <button type="button" className="px-lnk" onClick={() => chooseBucket(bucket)}>
                Clear filter
              </button>
            ) : null}
            {lens === 'dash' ? null : (
              <button type="button" className="px-lnk" onClick={toggleAll}>
                {allOpen ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </span>
        </div>
      </div>

      {/* SCROLLS: the data only */}
      <div className="px-body">
        {lens === 'dash' ? (
          dash()
        ) : !visible.length ? (
          <div className="px-emp">
            {bucket ? 'Nothing in this bucket.' : 'Nothing needs attention in the next 30 days.'}
          </div>
        ) : lens === 'flat' ? (
          /* A flat list has no parent group, so an indent would be marking a hierarchy that is
             not there. Rendered as a plain table. */
          <div className="px-grp open px-flat">
            <div className="px-gb">{table(visible)}</div>
          </div>
        ) : (
          groups.map((g) => {
            const o = allOpen || !!open[g.key];
            const chip = (v: number, c: string, l: string) =>
              v ? (
                <span className="px-chip" style={{ '--c': c } as React.CSSProperties} key={l}>
                  <span className="cv">{v}</span>
                  <span className="cl">{l}</span>
                </span>
              ) : null;
            return (
              <div className={`px-grp${o ? ' open' : ''}`} key={g.key}>
                <button
                  type="button"
                  className="px-gh"
                  aria-expanded={o}
                  onClick={() => toggleGroup(g.key)}
                >
                  <span className="px-car">▶</span>
                  {lens === 'person' ? <span className="px-ini">{pxIni(g.key)}</span> : null}
                  <span className="px-who">
                    <span className="n">{g.key}</span>
                    <span className="r">{g.sub}</span>
                  </span>
                  <span className="px-chips">
                    {chip(g.overdue, '#b3261e', 'Overdue')}
                    {chip(g.urgent, '#b26a00', 'Urgent')}
                    {chip(g.soon, '#1565c0', 'Soon')}
                    {chip(g.upcoming, '#2e6b3e', 'Upcoming')}
                  </span>
                </button>
                <div className="px-gb">{table(g.rows)}</div>
              </div>
            );
          })
        )}
        <p className="px-note">
          <strong>Overdue</strong> due date passed · <strong>Urgent</strong> due within 7 days ·{' '}
          <strong>Due soon</strong> 8–14 days · <strong>Upcoming</strong> 15–30 days ·{' '}
          <strong>Not updated</strong> untouched for {staleDays} days. An item is open while
          progress is below 100%. Counts cover the delivery projects you have access to.
        </p>
      </div>

      {/* ---- work-item drawer ---- */}
      <div className={`px-ovl${cur ? ' on' : ''}`} onClick={closeDrawer} />
      <aside className={`px-dw${cur ? ' on' : ''}`} role="dialog" aria-label="Work item">
        {cur ? (
          <>
            <div className="px-dwh">
              <span className={`px-tg ${cur.t === 'Activity' ? 'A' : 'D'}`}>
                {cur.t === 'Activity' ? 'A' : 'D'}
              </span>
              <span className="t" title={cur.n}>
                {cur.n}
              </span>
              <button className="px-dwx" onClick={closeDrawer} aria-label="Close">
                ×
              </button>
            </div>
            <div className="px-dwb">
              <div className="px-dws">
                <h3>Where it sits</h3>
                <dl className="px-meta">
                  <dt>Project</dt>
                  <dd>{cur.pj}</dd>
                  <dt>Initiative</dt>
                  <dd>{cur.op ? `${cur.op}${cur.on ? ` — ${cur.on}` : ''}` : 'Project-level'}</dd>
                  <dt>Phase</dt>
                  {/* Context, not an identifier — muted on purpose (§13.3). */}
                  <dd className="muted">{s(cur.ph).replace(/_/g, ' ')}</dd>
                  <dt>Owner</dt>
                  <dd className="muted">
                    {cur.pe}
                    {cur.ro ? ` (${cur.ro})` : ''}
                  </dd>
                </dl>
              </div>
              <div className="px-dws">
                <h3>Progress</h3>
                {/*
                  §13.3 — A NUMBER INPUT, NOT A SLIDER. The range control could not be
                  constrained inside the panel, and removing it in the source is what took the
                  write-back line with it. `step` is 1, not 5: a plan records the percentage
                  someone reports, not the nearest multiple of five.
                */}
                <div className="px-f">
                  <label htmlFor="pxDwPct">% complete</label>
                  <div className="px-pctrow">
                    <input
                      id="pxDwPct"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      inputMode="numeric"
                      className="px-pctnum"
                      value={dwPct}
                      onChange={(e) => setPct(e.target.value)}
                    />
                    <span className="px-pctpc">%</span>
                  </div>
                </div>
                {/* The derivation is shown so it is not a hidden rule — and while the field is
                    empty it says so plainly rather than reading as Not Started, which is what
                    would make an accidental zero look like a legitimate value. */}
                <p className="px-derived">
                  {pctNum === null ? (
                    <b style={{ color: '#c62828' }}>
                      Enter a number from 0 to 100 — nothing is saved until you do.
                    </b>
                  ) : (
                    <>
                      Status follows the percentage:{' '}
                      <b style={{ color: derivedColour(pctNum) }}>{derived(pctNum)}</b>{' '}
                      <span style={{ color: '#93a3b1' }}>
                        (0 = Not Started · 1–99 = In Progress · 100 = Done)
                      </span>
                    </>
                  )}
                </p>
              </div>
              <div className="px-dws">
                <h3>Dates</h3>
                {/*
                  START DATE IS READ-ONLY, and that is the source's behaviour rather than a
                  choice made here. Its input carries no dirty handler and it is absent from
                  the save payload (`exceptions.ejs:294` and `:648`), so editing it in the
                  source silently discards the edit. Reproducing an input that looks editable
                  and is not would be reproducing a data-loss trap, so it is rendered disabled:
                  the value shown and the value saved now agree. Logged in DEVIATIONS.
                */}
                <div className="px-f">
                  <label htmlFor="pxDwStart">Start date</label>
                  <input
                    id="pxDwStart"
                    type="date"
                    value={cur.sd ?? ''}
                    disabled
                    title="Set on the Work Item Definition screen"
                    readOnly
                  />
                </div>
                <div className="px-f" style={{ marginBottom: 0 }}>
                  <label htmlFor="pxDwDue">Delivered by</label>
                  <input
                    id="pxDwDue"
                    type="date"
                    value={dwDue}
                    onChange={(e) => {
                      setDwDue(e.target.value);
                      setDirty(true);
                    }}
                  />
                </div>
              </div>
              <div className="px-dws">
                <h3>Notes</h3>
                <div className="px-f" style={{ marginBottom: 0 }}>
                  <textarea
                    placeholder="What is holding this up?"
                    value={dwNotes}
                    onChange={(e) => {
                      setDwNotes(e.target.value);
                      setDirty(true);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="px-dwf">
              <button
                className="px-sv"
                onClick={() => void save()}
                disabled={!dirty || saving || pctNum === null}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="px-cn" onClick={closeDrawer}>
                Cancel
              </button>
              {/* The one place this screen navigates — for when the full programme context is
                  genuinely wanted. */}
              <a className="px-ecc" href={eccHref(cur)}>
                Open in Control Center →
              </a>
            </div>
          </>
        ) : null}
      </aside>

      {toast ? (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 26,
            transform: 'translateX(-50%)',
            background: '#1c2733',
            color: '#fff',
            padding: '9px 17px',
            borderRadius: 5,
            fontSize: 12.5,
            zIndex: 1300,
            boxShadow: '0 4px 18px rgba(0,0,0,.3)',
          }}
        >
          {toast}
        </div>
      ) : null}
    </div>
  );
}
